import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { exportStaffingPlan, getStaffingPlan, listScenarios } from '../../api/client'
import { currentLocale } from '../../i18n'
import { useAuth } from '../../context/AuthContext'
import { useCostLock } from './costLock'
import CostUnlock from './CostUnlock'
import VisibilityMenu from './VisibilityMenu'
import {
  byExplicitOrder, crossFilter, money, num, projectAlias, projectCode, projectTitle,
  reordered,
} from './sheet'

/**
 * "Disponibilidad -- por proyecto": the committed staffing plan across the
 * whole company. One column per project, one row per engineer, each cell the
 * sum of the hours that person is committed to that project over the period.
 *
 * The sibling of Balance mensual, asked of the other plan. Balance reports
 * what weekly planning RECORDED; this reports what the projects PROMISED
 * (`ProjectStaffingWeek`), which is what makes it the place to see who is
 * over-committed before the weeks arrive rather than after.
 *
 * Diferencia is Disponible - Total, and Disponible is the person's own working
 * day over the period's business days, minus known vacation and leave. Their
 * own day, not a flat eight hours: a crew row carries a deliberately larger
 * week (Alpha and Omega are loaded at 120 h) and a flat day would paint them
 * permanently red.
 *
 * The two thresholds are the reader's, not ours. Somebody planning a quarter
 * wants a wider warning band than somebody checking next month, so how much
 * slack still counts as comfortable is a setting, remembered per browser.
 */
// Roughly a month, a quarter and a half year. Dotacion is planned and edited
// by the week, so the window is counted in weeks: no week is ever split
// between two windows, and "available" is an exact business-day count rather
// than a calendar month that happens to start mid-week.
const WINDOWS = [5, 15, 28]

const mondayOf = (d) => {
  const out = new Date(d)
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7))   // Sunday counts as 7
  out.setHours(12)                    // midday, so a DST shift cannot slip a day
  return out
}
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  + `-${String(d.getDate()).padStart(2, '0')}`
const shortDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`)
  return d.toLocaleDateString(currentLocale(), { day: '2-digit', month: 'short' })
}

const PCT_KEYS = { slack: 'capacity.staffing.slackPct', budget: 'capacity.staffing.budgetPct' }
const DEFAULT_PCT = 10

function readPct(key) {
  try {
    const raw = window.localStorage.getItem(key)
    const value = Number(raw)
    return raw != null && Number.isFinite(value) && value >= 0 ? value : DEFAULT_PCT
  } catch {
    return DEFAULT_PCT      // a browser with storage blocked must still render
  }
}

function writePct(key, value) {
  try {
    window.localStorage.setItem(key, String(value))
  } catch {
    // Remembering the preference is a courtesy; failing to is not an error.
  }
}

/**
 * Green with room to spare, amber running tight, red over-committed.
 * `margin` is what is left; `reference` is what it is judged against (the
 * person's available hours, or the project's budget). No reference means
 * nobody set a budget -- which is not the same as no room, so it gets no
 * colour at all rather than a reassuring green.
 */
function toneFor(margin, reference, pct) {
  if (margin == null || reference == null || !reference) return 'text-gray-500'
  if (margin < 0) return 'text-red-600 font-medium'
  if (margin <= reference * (pct / 100)) return 'text-amber-600 font-medium'
  return 'text-emerald-700 font-medium'
}

export default function StaffingMatrix() {
  const { t } = useTranslation(['clevel', 'common'])
  const { password, unlocked, lock } = useCostLock()
  const { user } = useAuth()
  const [weeks, setWeeks] = useState(WINDOWS[0])
  const [start, setStart] = useState(() => isoDay(mondayOf(new Date())))
  const [costBasis, setCostBasis] = useState('company')
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [asking, setAsking] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [slackPct, setSlackPct] = useState(() => readPct(PCT_KEYS.slack))
  const [budgetPct, setBudgetPct] = useState(() => readPct(PCT_KEYS.budget))
  const [hiddenProjects, setHiddenProjects] = useState(() => new Set())
  const [hiddenMembers, setHiddenMembers] = useState(() => new Set())
  const [projectOrder, setProjectOrder] = useState([])
  const [memberOrder, setMemberOrder] = useState([])
  const [dragging, setDragging] = useState(null)
  const [scenarioId, setScenarioId] = useState('')
  const [scenarios, setScenarios] = useState([])

  const k = useCallback((suffix, opts) => t(`capacity.staffing.${suffix}`, opts), [t])

  // The dotacion editor lives on the project page, which engineering roles can
  // open and a pure c_level cannot. Hiding the link beats sending somebody to
  // a route that bounces them.
  const roles = user?.roles || (user?.role ? [user.role] : [])
  const canOpenProject = ['admin', 'pm_po', 'engineer'].some(r => roles.includes(r))
  // c_level EXACTLY -- mirrors the backend's require_exactly("c_level") gate
  // on scenario_id (routers/capacity.py). An admin (without c_level) never
  // even fetches the scenario list, let alone sees the selector.
  const canScenarios = roles.includes('c_level')

  useEffect(() => {
    if (!canScenarios) return
    listScenarios().then(setScenarios).catch(() => {})
  }, [canScenarios])

  const load = useCallback(() => {
    getStaffingPlan({ weeks, start, costBasis, scenarioId: canScenarios ? scenarioId : '' }, password)
      .then(d => { setData(d); setError('') })
      .catch(err => setError(err.response?.data?.detail || k('loadFailed')))
  }, [weeks, start, costBasis, scenarioId, canScenarios, password, k])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!fullscreen) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [fullscreen])

  // A different window can hold different projects and people, so whatever
  // order somebody arranged for this one does not carry over.
  const clearOrder = () => { setProjectOrder([]); setMemberOrder([]) }

  const goWindow = (size) => { setWeeks(size); clearOrder() }

  // One whole window at a time, so consecutive views butt up against each
  // other instead of overlapping.
  const shift = (delta) => {
    clearOrder()
    const d = new Date(`${start}T12:00:00`)
    d.setDate(d.getDate() + delta * weeks * 7)
    setStart(isoDay(d))
  }

  const setPct = (which, raw) => {
    const value = Math.max(0, Number(raw) || 0)
    if (which === 'slack') { setSlackPct(value); writePct(PCT_KEYS.slack, value) }
    else { setBudgetPct(value); writePct(PCT_KEYS.budget, value) }
  }

  const toggleHiddenProject = (id) => setHiddenProjects(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const toggleHiddenMember = (id) => setHiddenMembers(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  if (error) return <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>
  if (!data) return <p className="text-sm text-gray-400 px-1">{t('capacity.loading')}</p>

  const currency = data.cost_currency
  const showMoney = !!data.costs_unlocked
  const allProjects = byExplicitOrder(data.projects, projectOrder, p => p.project_id)
  const allRows = byExplicitOrder(data.rows, memberOrder, r => r.team_member_id)
  const hours = (r, p) => r.by_project[p.project_id] || 0
  const { rows: visibleRows, projects: visibleProjects } = crossFilter(
    allRows.filter(r => !hiddenMembers.has(r.team_member_id)),
    allProjects.filter(p => !hiddenProjects.has(p.project_id)),
    hours)
  // The synthetic "room nobody has booked yet" column -- see
  // staffing_matrix.UNUSED_KEY. It carries no name/code of its own, so the
  // label lives here instead of in projectTitle/projectCode.
  const colTitle = (p) => p.is_unused ? k('unusedProject') : projectTitle(p)
  const colCode = (p) => p.is_unused ? k('unusedProjectShort') : projectCode(p)

  // Seeded on the first drag rather than up front, so no effect has to write
  // state just to have something to reorder.
  const onDrop = (axis, toId) => {
    if (!dragging || dragging.axis !== axis || dragging.id === toId) return
    if (axis === 'project') {
      setProjectOrder(o => reordered(
        o.length ? o : allProjects.map(p => p.project_id), dragging.id, toId))
    } else {
      setMemberOrder(o => reordered(
        o.length ? o : allRows.map(r => r.team_member_id), dragging.id, toId))
    }
    setDragging(null)
  }
  const dragProps = (axis, id) => ({
    draggable: true,
    onDragStart: () => setDragging({ axis, id }),
    onDragEnd: () => setDragging(null),
    onDragOver: (e) => { if (dragging?.axis === axis) e.preventDefault() },
    onDrop: () => onDrop(axis, id),
  })

  // Straight off the payload, so the label can never claim a window the
  // figures below it were not built from.
  const label = `${shortDay(data.start)} – ${shortDay(data.end)}`

  const sumRows = (key) => visibleRows.reduce((s, r) => s + (r[key] || 0), 0)
  const sumProject = (projectId) => visibleRows.reduce(
    (s, r) => s + (r.by_project[projectId] || 0), 0)
  const projectCost = (projectId) => visibleRows.reduce((s, r) => {
    const own = r.by_project[projectId] || 0
    if (!own || !r.total || r.cost_amount == null) return s
    // The API prices a person, not a person-project, so their cost follows
    // their hours across the projects those hours are on.
    return s + (r.cost_amount * own) / r.total
  }, 0)

  const headCell = 'sticky top-0 z-20 bg-white border-b border-gray-200 px-2 py-2 text-right whitespace-nowrap'
  const footCell = 'px-2 py-2 text-right tabular-nums border-t border-gray-300'

  return (
    <div className={fullscreen
      ? 'fixed inset-0 z-40 bg-gray-50 overflow-auto p-4 space-y-3'
      : 'space-y-4'}>
      {asking && <CostUnlock onClose={() => setAsking(false)} />}

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          {WINDOWS.map(size => (
            <button key={size} onClick={() => goWindow(size)}
              title={k(`window_${size}_hint`)}
              className={`px-3 py-1.5 ${weeks === size ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {k(`window_${size}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">‹</button>
          <span className="text-sm font-semibold text-gray-900 px-2 min-w-40 text-center">
            {label}
          </span>
          <button onClick={() => shift(1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">›</button>
        </div>

        {canScenarios && (
          <select
            value={scenarioId}
            onChange={e => setScenarioId(e.target.value)}
            className={`text-xs border rounded-lg px-2 py-1.5 bg-white ${
              scenarioId ? 'border-amber-300 text-amber-800' : 'border-gray-200 text-gray-700'}`}
          >
            <option value="">{k('realPlan')}</option>
            {scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}

        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          {['company', 'client'].map(basis => (
            <button key={basis} onClick={() => setCostBasis(basis)}
              className={`px-3 py-1.5 ${costBasis === basis ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {k(`basis_${basis}`)}
            </button>
          ))}
        </div>

        <VisibilityMenu
          label={k('showProjects')}
          items={allProjects}
          hidden={hiddenProjects}
          getKey={p => p.project_id}
          getLabel={p => colTitle(p)}
          getHint={p => num(p.planned)}
          onToggle={toggleHiddenProject}
          onShowAll={() => setHiddenProjects(new Set())}
          onHideAll={() => setHiddenProjects(new Set(allProjects.map(p => p.project_id)))}
          allLabel={k('showAll')}
          noneLabel={k('showNone')}
          emptyLabel={k('noStaffing')}
        />
        <VisibilityMenu
          label={k('showPeople')}
          items={allRows}
          hidden={hiddenMembers}
          getKey={r => r.team_member_id}
          getLabel={r => r.name}
          getHint={r => num(r.total)}
          onToggle={toggleHiddenMember}
          onShowAll={() => setHiddenMembers(new Set())}
          onHideAll={() => setHiddenMembers(new Set(allRows.map(r => r.team_member_id)))}
          allLabel={k('showAll')}
          noneLabel={k('showNone')}
          emptyLabel={k('noStaffing')}
        />

        <label className="flex items-center gap-1 text-xs text-gray-600"
               title={k('slackPctHint')}>
          {k('slackPct')}
          <input type="number" min="0" max="100" value={slackPct}
                 onChange={e => setPct('slack', e.target.value)}
                 className="w-14 border border-gray-200 rounded px-1.5 py-1 text-xs text-right" />%
        </label>
        <label className="flex items-center gap-1 text-xs text-gray-600"
               title={k('budgetPctHint')}>
          {k('budgetPct')}
          <input type="number" min="0" max="100" value={budgetPct}
                 onChange={e => setPct('budget', e.target.value)}
                 className="w-14 border border-gray-200 rounded px-1.5 py-1 text-xs text-right" />%
        </label>

        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setFullscreen(v => !v)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
            {fullscreen ? `⤡ ${k('fullscreenExit')}` : `⤢ ${k('fullscreen')}`}
          </button>
          <button
            onClick={() => exportStaffingPlan({ weeks, start, costBasis }, password)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
            {k('export')}
          </button>
          {unlocked ? (
            <button onClick={lock}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
              🔓 {k('lock')}
            </button>
          ) : (
            <button onClick={() => setAsking(true)}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
              🔒 {k('showCosts')}
            </button>
          )}
        </div>
      </div>

      {!data.costs_unlocked && (
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          {k('costsLocked')}
        </p>
      )}

      <div className={`bg-white rounded-xl border border-gray-200 overflow-auto ${
        fullscreen ? 'max-h-[calc(100vh-13rem)]' : 'max-h-[65vh]'}`}>
        <table className="w-full text-xs border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="sticky top-0 left-0 z-30 bg-white border-b border-gray-200 text-left px-3 py-2 font-semibold text-gray-600">
                {k('person')}
              </th>
              {visibleProjects.map(p => (
                <th key={p.project_id} {...dragProps('project', p.project_id)}
                    className={`${headCell} !text-center font-medium ${
                      p.is_unused ? 'text-emerald-700 bg-emerald-50/60' : 'text-gray-500'} ${
                      dragging?.axis === 'project' && dragging.id === p.project_id
                        ? 'opacity-40' : ''}`}
                    title={`${colTitle(p)} — ${k('dragHint')}`}>
                  <div className="flex items-center justify-center gap-1 leading-tight">
                    {colCode(p)}
                    {canOpenProject && !p.is_unused && (
                      <a href={`/projects/${p.project_id}#staffing`}
                         target="_blank" rel="noopener noreferrer"
                         title={k('openStaffing')}
                         onClick={e => e.stopPropagation()}
                         draggable={false}
                         className="text-gray-300 hover:text-gray-900 no-underline">↗</a>
                    )}
                  </div>
                  {!p.is_unused && projectAlias(p) && (
                    <div className="text-[10px] font-normal text-gray-400 leading-tight max-w-24 truncate mx-auto">
                      {projectAlias(p)}
                    </div>
                  )}
                </th>
              ))}
              <th className={`${headCell} font-semibold text-gray-600`}>{k('total')}</th>
              <th className={`${headCell} font-medium text-gray-500`}>{k('available')}</th>
              <th className={`${headCell} font-medium text-gray-500`}>{k('difference')}</th>
              {showMoney && (
                <th className={`${headCell} font-medium text-gray-500`}>
                  {k('cost', { currency })}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => (
              <tr key={row.team_member_id} className={i % 2 ? 'bg-gray-50/60' : 'bg-white'}>
                <td {...dragProps('member', row.team_member_id)}
                    className={`sticky left-0 z-10 bg-inherit px-3 py-1.5 text-gray-800 whitespace-nowrap ${
                      dragging?.axis === 'member' && dragging.id === row.team_member_id
                        ? 'opacity-40' : ''}`}
                    title={k('dragHint')}>
                  {row.name}
                </td>
                {visibleProjects.map(p => (
                  <td key={p.project_id} className={`px-2 py-1.5 text-right tabular-nums ${
                    p.is_unused ? 'text-emerald-700 bg-emerald-50/40' : 'text-gray-700'}`}>
                    {num(row.by_project[p.project_id])}
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right tabular-nums font-medium text-gray-900">
                  {num(row.total)}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-gray-500"
                    title={k('availableHint', {
                      vacation: num(row.vacation), leave: num(row.leave) })}>
                  {num(row.available)}
                </td>
                <td className={`px-2 py-1.5 text-right tabular-nums ${
                  toneFor(row.difference, row.available, slackPct)}`}>
                  {num(row.difference)}
                </td>
                {showMoney && (
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                    {money(row.cost_amount, currency)}
                  </td>
                )}
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={1 + visibleProjects.length + 3 + (showMoney ? 1 : 0)}
                    className="px-4 py-8 text-center text-gray-400">
                  {k('noStaffing')}
                </td>
              </tr>
            )}
          </tbody>
          {visibleRows.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                <td className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-gray-900 border-t border-gray-300">
                  {k('totalRow')}
                </td>
                {visibleProjects.map(p => (
                  <td key={p.project_id} className={`${footCell} text-gray-800`}>
                    {num(sumProject(p.project_id))}
                  </td>
                ))}
                <td className={`${footCell} text-gray-900`}>{num(sumRows('total'))}</td>
                <td className={`${footCell} text-gray-800`}>{num(sumRows('available'))}</td>
                <td className={`${footCell} ${
                  toneFor(sumRows('difference'), sumRows('available'), slackPct)}`}>
                  {num(sumRows('difference'))}
                </td>
                {showMoney && (
                  <td className={`${footCell} text-gray-800`}>
                    {money(visibleRows.reduce((s, r) => s + (r.cost_amount || 0), 0), currency)}
                  </td>
                )}
              </tr>

              <tr className="bg-gray-50 text-gray-500">
                <td className="sticky left-0 z-10 bg-gray-50 px-3 py-1.5">{k('budgetRow')}</td>
                {visibleProjects.map(p => (
                  <td key={p.project_id} className="px-2 py-1.5 text-right tabular-nums">
                    {num(p.hours_budget)}
                  </td>
                ))}
                <td colSpan={3 + (showMoney ? 1 : 0)} />
              </tr>

              <tr className="bg-gray-50 font-semibold">
                <td className="sticky left-0 z-10 bg-gray-50 px-3 py-1.5 text-gray-900">
                  {k('budgetDiffRow')}
                </td>
                {visibleProjects.map(p => {
                  // Against what is on screen, so hiding a person changes the
                  // total AND the margin it is judged by, consistently.
                  const margin = p.hours_budget == null
                    ? null : p.hours_budget - sumProject(p.project_id)
                  return (
                    <td key={p.project_id}
                        className={`px-2 py-1.5 text-right tabular-nums ${
                          toneFor(margin, p.hours_budget, budgetPct)}`}
                        title={p.hours_budget == null ? k('noBudget') : undefined}>
                      {num(margin)}
                    </td>
                  )
                })}
                <td colSpan={3 + (showMoney ? 1 : 0)} />
              </tr>

              {showMoney && (
                <tr className="bg-gray-50 text-gray-500">
                  <td className="sticky left-0 z-10 bg-gray-50 px-3 py-1.5">
                    {k('cost', { currency })}
                  </td>
                  {visibleProjects.map(p => (
                    <td key={p.project_id} className="px-2 py-1.5 text-right tabular-nums">
                      {p.is_unused ? '—' : money(projectCost(p.project_id), currency)}
                    </td>
                  ))}
                  <td colSpan={4} />
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>

      {data.members_missing_rate?.length > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {k('missingRates', { names: data.members_missing_rate.join(', ') })}
        </p>
      )}

      <p className="text-[11px] text-gray-400">
        {k('legend', {
          weeks: data.weeks, days: num(data.business_days, 0),
          slack: slackPct, budget: budgetPct,
        })}
      </p>
    </div>
  )
}
